import React from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useLocation} from 'react-router-dom'

import {Grid, CircularProgress, Box} from '@mui/material'
import HTMLReactParser from 'html-react-parser'

import {useConsultationsProgramDetailQuery} from '@progyny/generated/graphql'
import OverviewCard from '@progyny/pages/Consultations/components/OverviewCard'

interface ProgramCardProps {
  slug: string
  isAskAMidwife?: boolean
  icon: React.ComponentType<any>
  color: 'white' | 'bright' | 'dark'
  colors: any
  fullWidth?: boolean
  locked?: boolean
  isFullRow?: boolean
}

const ProgramCard: React.FC<ProgramCardProps> = ({
  slug,
  isAskAMidwife = false,
  icon,
  color,
  colors,
  fullWidth = false,
  locked = false,
  isFullRow = false,
}) => {
  const history = useHistory()
  const location = useLocation()
  const {t} = useTranslation()
  const {data, loading} = useConsultationsProgramDetailQuery({
    variables: {slug},
  })

  if (loading) {
    return (
      <Grid item xs={12} md={fullWidth ? 12 : 6}>
        <OverviewCard
          content={
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              width='100%'
            >
              <CircularProgress size={40} sx={{color: colors?.primary}} />
            </Box>
          }
          color={color}
          colors={colors}
          minHeight={200}
        />
      </Grid>
    )
  }

  if (!data?.consultations?.program) return null

  const program = data.consultations.program
  const canEnrollInPrograms = program.consultation?.canEnrollInPrograms ?? false

  const enableLearnMore = program.enableLearnMoreContent ?? true

  return (
    <Grid item xs={12} md={fullWidth ? 12 : 6}>
      <OverviewCard
        icon={icon}
        title={program.title || ''}
        content={HTMLReactParser(program.previewDescription || '')}
        primaryButton={{
          label: isAskAMidwife
            ? t('consultations_program_session_plan_book_call')
            : t('consultations_program_session_plan_enroll_now'),
          onClick: () => {
            history.push(`/consultations/program/${program.slug}`)
          },
          disabled: !canEnrollInPrograms,
        }}
        secondaryButton={
          enableLearnMore
            ? {
                label: t('common_learn_more'),
                onClick: () => {
                  history.push(
                    `/consultations/program/${program.slug}/info/1`,
                    {
                      referrer: location.pathname,
                    }
                  )
                },
              }
            : undefined
        }
        color={color}
        colors={colors}
        locked={locked}
        isFullRow={isFullRow}
      />
    </Grid>
  )
}

export default ProgramCard
